import { useState, useEffect, useCallback } from 'react';
import { Profile, Report, SuccessStory, AdminUser, AdminStats, PayoutRequest } from './lib/types';
import adminService from './services/adminService';
import { DashboardStats } from './components/dashboard-stats';
import { UserManagement } from './components/user-management';
import { ReportsManagement } from './components/reports-management';
import { AgentPayouts } from './components/agent-payouts';
import { SuccessStoriesModeration } from './components/success-stories-moderation';
import { SupportTickets } from './components/support-tickets';
import { PlansManagement } from './components/plans-management';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Toaster } from './components/ui/sonner';
import {
  LayoutDashboard, Users, AlertTriangle, DollarSign, Heart,
  LogOut, RefreshCw, CheckCircle2, Loader2,
  Headphones, TrendingUp, WifiOff, Trash2, AlertOctagon, Crown,
} from 'lucide-react';
import { toast } from 'sonner';

interface AdminAppProps {
  onLogout: () => void;
}

// ─── Empty defaults ────────────────────────────────────────────────────────────
const EMPTY_STATS: AdminStats = {
  totalUsers: 0,
  freeUsers: 0,
  paidUsers: 0,
  agents: 0,
  totalProfiles: 0,
  verifiedProfiles: 0,
  pendingVerifications: 0,
  totalReports: 0,
  pendingReports: 0,
  totalMatches: 0,
  successStories: 0,
  pendingStories: 0,
  revenue: 0,
  agentCommissions: 0,
};

const DEFAULT_ADMIN: AdminUser = {
  id: '',
  name: 'Admin',
  email: '',
  role: 'admin',
};

// ─── Safe date parser ─────────────────────────────────────────────────────────
const safeDate = (value: any): Date => {
  if (!value) return new Date();
  const d = new Date(value);
  return isNaN(d.getTime()) ? new Date() : d;
};

// ─── API response normalizers ──────────────────────────────────────────────────
const normalizeProfile = (p: any): Profile => {
  // getUsers returns User documents with a populated 'profile' sub-doc.
  // Use the Profile's _id for API calls (verify endpoint needs Profile _id).
  const prof = p.profile || {};
  return {
    ...prof,
    // User-level fields override profile fields where both exist
    email: p.email || prof.email || '',
    userType: p.userType || prof.userType || 'free',
    // Use Profile _id — this is what /admin/profiles/:id/verify expects
    id: prof._id || p._id || p.id || '',
    _id: prof._id || p._id || '',
    userId: p._id || '',
    name: prof.name || p.name || '',
    phone: prof.phone || p.phone || '',
    about: prof.about || '',
    profilePicture: prof.profilePicture || p.profilePicture || '',
    likedProfiles: prof.likedProfiles || [],
    dislikedProfiles: prof.dislikedProfiles || [],
    verification: prof.verification || { phone: 'pending', email: 'pending', driverLicense: 'pending' },
    age: prof.age || 0,
    height: prof.height || '',
    weight: prof.weight || '',
    complexion: prof.complexion || '',
    bodyType: prof.bodyType || '',
    eyeColor: prof.eyeColor || '',
    location: prof.location || '',
    school: prof.school || '',
    college: prof.college || '',
    qualification: prof.qualification || '',
    job: prof.job || '',
    company: prof.company || '',
    interests: prof.interests || [],
    lookingFor: prof.lookingFor || 'marriage',
    lookingForDescription: prof.lookingForDescription || '',
    isSearchable: prof.isSearchable ?? true,
  };
};

const normalizeReport = (r: any): Report => ({
  id: r._id || r.id || '',
  reporterId:
    typeof r.reporter === 'object' ? r.reporter?._id || '' : r.reporter || r.reporterId || '',
  reportedProfileId:
    typeof r.reportedProfile === 'object'
      ? r.reportedProfile?._id || ''
      : r.reportedProfile || r.reportedProfileId || '',
  reason: r.reason || 'No reason provided',
  timestamp: safeDate(r.createdAt || r.timestamp),
  status: r.status || 'pending',
  // carry full populated objects for display
  reporterData: typeof r.reporter === 'object' ? r.reporter : undefined,
  reportedData: typeof r.reportedProfile === 'object' ? r.reportedProfile : undefined,
} as any);

const normalizeStory = (s: any): SuccessStory => ({
  id: s._id || s.id || '',
  profile1: normalizeProfile(
    s.profile1 || { _id: 'unknown', name: 'Unknown', userType: 'free' },
  ),
  profile2: normalizeProfile(
    s.profile2 || { _id: 'unknown', name: 'Unknown', userType: 'free' },
  ),
  story: s.story || '',
  weddingDate: safeDate(s.weddingDate),
  images: Array.isArray(s.images) ? s.images : [],
  approved: s.approved ?? false,
});

const normalizePayout = (p: any): PayoutRequest => ({
  id: p._id || p.id || '',
  agentId:
    typeof p.agent === 'object' ? p.agent?._id || '' : p.agent || p.agentId || '',
  agentName: p.agentName || p.agent?.name || 'Unknown Agent',
  amount: typeof p.amount === 'number' ? p.amount : 0,
  requestedDate: safeDate(p.createdAt || p.requestedDate),
  status: p.status || 'pending',
  processedDate: p.processedAt ? safeDate(p.processedAt) : undefined,
});

const normalizeTicket = (t: any) => ({
  id: t._id || t.id || '',
  _id: t._id,
  subject: t.subject || '(No subject)',
  category: t.category || 'General',
  status: (t.status || 'open') as 'open' | 'in-progress' | 'resolved' | 'closed',
  priority: t.priority as 'low' | 'medium' | 'high' | undefined,
  createdAt: safeDate(t.createdAt),
  updatedAt: t.updatedAt ? safeDate(t.updatedAt) : undefined,
  user: t.user ? { name: t.user.name || '', email: t.user.email || '' } : undefined,
  messages: Array.isArray(t.messages)
    ? t.messages.map((m: any) => ({
        sender: m.sender || 'user',
        content: m.content || '',
        createdAt: safeDate(m.createdAt),
      }))
    : [],
  description: t.description || t.message || '',
});

// ─── Component ────────────────────────────────────────────────────────────────
function AdminApp({ onLogout }: AdminAppProps) {
  const [currentAdmin] = useState<AdminUser>(
    () => adminService.getAdminInfo() ?? DEFAULT_ADMIN,
  );

  const [stats, setStats] = useState<AdminStats>(EMPTY_STATS);
  const [chartData, setChartData] = useState<any>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [successStories, setSuccessStories] = useState<SuccessStory[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [apiConnected, setApiConnected] = useState<boolean | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const [clearingData, setClearingData] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ── Load all data from API ──────────────────────────────────────────────────
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);

    try {
      const [statsRes, usersRes, reportsRes, payoutsRes, storiesRes, ticketsRes] =
        await Promise.allSettled([
          adminService.getStats(),
          adminService.getUsers({ limit: '200' }),
          adminService.getReports({ limit: '200' }),
          adminService.getPayouts({ limit: '200' }),
          adminService.getStories({ limit: '200' }),
          adminService.getTickets({ limit: '200' }),
        ]);

      let anySuccess = false;

      if (statsRes.status === 'fulfilled') {
        const s = statsRes.value?.stats;
        const charts = statsRes.value?.charts;
        if (s) {
          setStats({
            totalUsers: s.totalUsers ?? 0,
            freeUsers: s.freeUsers ?? 0,
            paidUsers: s.paidUsers ?? 0,
            agents: s.agents ?? 0,
            totalProfiles: s.totalProfiles ?? 0,
            verifiedProfiles: s.verifiedProfiles ?? 0,
            pendingVerifications: s.pendingVerifications ?? 0,
            totalReports: s.totalReports ?? 0,
            pendingReports: s.pendingReports ?? 0,
            totalMatches: s.totalMatches ?? 0,
            successStories: s.successStories ?? 0,
            pendingStories: s.pendingStories ?? 0,
            revenue: s.revenue ?? 0,
            agentCommissions: s.agentCommissions ?? 0,
          });
        }
        if (charts) {
          const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const revenueByMonth = (charts.monthlyRevenue || []).map((d: any) => ({
            month: MONTHS[(d._id - 1) % 12] || String(d._id),
            revenue: d.total || 0,
            users: (charts.monthlyUsers || []).find((u: any) => u._id === d._id)?.count || 0,
          }));
          setChartData({ ...charts, revenueByMonth });
        }
        anySuccess = true;
      }

      if (usersRes.status === 'fulfilled') {
        const users = usersRes.value?.users || usersRes.value?.data || [];
        setProfiles(Array.isArray(users) ? users.map(normalizeProfile) : []);
        anySuccess = true;
      }

      if (reportsRes.status === 'fulfilled') {
        const reps = reportsRes.value?.reports || reportsRes.value?.data || [];
        setReports(Array.isArray(reps) ? reps.map(normalizeReport) : []);
        anySuccess = true;
      }

      if (payoutsRes.status === 'fulfilled') {
        const pays = payoutsRes.value?.payouts || payoutsRes.value?.data || [];
        setPayoutRequests(Array.isArray(pays) ? pays.map(normalizePayout) : []);
        anySuccess = true;
      }

      if (storiesRes.status === 'fulfilled') {
        const stories = storiesRes.value?.stories || storiesRes.value?.data || [];
        setSuccessStories(Array.isArray(stories) ? stories.map(normalizeStory) : []);
        anySuccess = true;
      }

      if (ticketsRes.status === 'fulfilled') {
        const ticks = ticketsRes.value?.tickets || ticketsRes.value?.data || [];
        setTickets(Array.isArray(ticks) ? ticks.map(normalizeTicket) : []);
        anySuccess = true;
      }

      if (!anySuccess) {
        const errMsg =
          (statsRes as PromiseRejectedResult).reason?.message ||
          (statsRes as PromiseRejectedResult).reason?.response?.data?.message ||
          'Unable to connect to API server';
        setApiConnected(false);
        setApiError(errMsg);
        if (!silent) toast.error(`API offline: ${errMsg}`);
      } else {
        setApiConnected(true);
      }

      setLastRefresh(new Date());
    } catch (err: any) {
      const msg = err?.message || 'Unexpected error during data load';
      setApiConnected(false);
      setApiError(msg);
      if (!silent) toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Standalone ticket refresh
  const loadTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await adminService.getTickets({ limit: '200' });
      const ticks = res?.tickets || res?.data || [];
      setTickets(Array.isArray(ticks) ? ticks.map(normalizeTicket) : []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to refresh tickets');
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => loadData(true), 60_000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ── Handlers — only toast on error; success toasts live in child components ──

  const handleUpdateProfile = async (updatedProfile: Profile) => {
    try {
      // PUT /admin/users/:id keys off the User _id, not the Profile _id.
      const profileId = (updatedProfile as any)._id || updatedProfile.id;
      const userId = (updatedProfile as any).userId || profileId;
      await adminService.updateUser(userId, { userType: updatedProfile.userType });
      setProfiles(prev =>
        prev.map(p => ((p as any)._id || p.id) === profileId ? updatedProfile : p),
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update profile');
      throw err; // re-throw so child can handle
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    try {
      // `profileId` is a Profile _id, but DELETE /admin/users/:id keys off the
      // User _id (User.findById). Resolve to the owning user id; fall back to the
      // given id for profile-less users, whose normalized id already is User _id.
      const target = profiles.find(
        p => (p as any)._id === profileId || p.id === profileId,
      );
      const userId = (target as any)?.userId || profileId;
      await adminService.deleteUser(userId);
      setProfiles(prev =>
        prev.filter(p => (p as any)._id !== profileId && p.id !== profileId),
      );
      setStats(prev => ({
        ...prev,
        totalUsers: Math.max(0, prev.totalUsers - 1),
        totalProfiles: Math.max(0, prev.totalProfiles - 1),
      }));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete user');
      throw err;
    }
  };

  const handleVerifyProfile = async (
    profileId: string,
    type: 'phone' | 'email' | 'driverLicense',
    approved: boolean,
  ) => {
    try {
      await adminService.verifyProfile(profileId, type, approved);
      setProfiles(prev =>
        prev.map(p => {
          const pId = (p as any)._id || p.id;
          if (pId === profileId || p.id === profileId) {
            return {
              ...p,
              verification: {
                ...p.verification,
                [type]: approved ? 'verified' : 'rejected',
              },
            };
          }
          return p;
        }),
      );
      if (approved) {
        setStats(prev => ({
          ...prev,
          pendingVerifications: Math.max(0, prev.pendingVerifications - 1),
          verifiedProfiles: prev.verifiedProfiles + 1,
        }));
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update verification');
      throw err;
    }
  };

  const handleBanUser = async (userId: string) => {
    await adminService.banUser(userId);
    setProfiles(prev =>
      prev.filter(p => (p as any)._id !== userId && p.id !== userId),
    );
  };

  const handleResolveReport = async (
    reportId: string,
    action: 'ban' | 'warn' | 'dismiss',
    notes?: string,
  ) => {
    try {
      await adminService.resolveReport(reportId, action, notes);
      setReports(prev =>
        prev.map(r => (r.id === reportId ? { ...r, status: 'resolved' } : r)),
      );
      setStats(prev => ({
        ...prev,
        pendingReports: Math.max(0, prev.pendingReports - 1),
      }));
      // NOTE: on action === 'ban' the backend resolve endpoint already bans the
      // reported user (User.findByIdAndUpdate(report.reportedUser)). We must NOT
      // make a second client-side ban call with `reportedProfileId` — that is a
      // Profile _id, which /admin/users/:id/ban would 404 on. The server handles it.
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resolve report');
      throw err;
    }
  };

  const handleProcessPayout = async (
    payoutId: string,
    action: 'approve' | 'reject',
    notes?: string,
  ) => {
    try {
      await adminService.processPayout(payoutId, action, notes);
      setPayoutRequests(prev =>
        prev.map(p =>
          p.id === payoutId
            ? {
                ...p,
                status: action === 'approve' ? ('approved' as const) : ('rejected' as const),
                processedDate: new Date(),
              }
            : p,
        ),
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to process payout');
      throw err;
    }
  };

  const handleApproveStory = async (storyId: string) => {
    try {
      await adminService.approveStory(storyId);
      setSuccessStories(prev =>
        prev.map(s => (s.id === storyId ? { ...s, approved: true } : s)),
      );
      setStats(prev => ({
        ...prev,
        pendingStories: Math.max(0, prev.pendingStories - 1),
        successStories: prev.successStories + 1,
      }));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to approve story');
      throw err;
    }
  };

  const handleRejectStory = async (storyId: string) => {
    try {
      await adminService.rejectStory(storyId);
      setSuccessStories(prev => prev.filter(s => s.id !== storyId));
      setStats(prev => ({
        ...prev,
        pendingStories: Math.max(0, prev.pendingStories - 1),
      }));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reject story');
      throw err;
    }
  };

  const handleDeleteStory = async (storyId: string) => {
    try {
      await adminService.deleteStory(storyId);
      setSuccessStories(prev => prev.filter(s => s.id !== storyId));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete story');
      throw err;
    }
  };

  const handleClearAllData = async () => {
    setClearingData(true);
    try {
      await adminService.clearAllData();
      // Reset all local state to empty
      setStats(EMPTY_STATS);
      setChartData(null);
      setProfiles([]);
      setReports([]);
      setPayoutRequests([]);
      setSuccessStories([]);
      setTickets([]);
      setShowClearConfirm(false);
      toast.success('All test data cleared — dashboard now shows live zeros.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to clear data');
    } finally {
      setClearingData(false);
    }
  };

  const pendingPayouts = payoutRequests.filter(p => p.status === 'pending').length;
  const openTickets = tickets.filter(
    t => t.status === 'open' || t.status === 'in-progress',
  ).length;

  const showContent = !isLoading || profiles.length > 0 || stats.totalUsers > 0 || apiConnected === false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/40 to-blue-50/40">
      <Toaster richColors />

      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 shadow-sm">
                <Heart className="size-6 text-pink-500 fill-pink-500" />
                <div className="text-lg font-bold">
                  <span className="text-blue-600">Dulha</span>
                  <span className="text-pink-500">Dulhun</span>
                </div>
              </div>
              <div className="border-l pl-3 border-gray-200">
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                  Admin Dashboard
                </h1>
                <p className="text-xs text-gray-500">Management Console</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              {/* Last refresh */}
              {apiConnected && (
                <div className="hidden md:flex items-center gap-1 text-xs text-gray-400">
                  <span>Updated {lastRefresh.toLocaleTimeString()}</span>
                </div>
              )}

              {/* Refresh */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => loadData()}
                disabled={isLoading}
                className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
              >
                <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>

              <div className="text-right hidden md:block">
                <p className="font-semibold text-sm text-gray-900">{currentAdmin.name}</p>
                <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 border-0 text-white text-xs">
                  {currentAdmin.role === 'super-admin' ? '👑 Super Admin' : 'Admin'}
                </Badge>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="border-purple-300 hover:bg-purple-50 hover:border-purple-400"
                onClick={onLogout}
              >
                <LogOut className="size-4 mr-0 sm:mr-1" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* API Offline Banner */}
      {apiConnected === false && apiError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="container mx-auto flex items-center gap-3">
            <WifiOff className="size-4 text-red-600 shrink-0" />
            <p className="text-sm text-red-800 flex-1">
              <span className="font-semibold">API Unavailable:</span> {apiError}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 shrink-0"
              onClick={() => loadData()}
              disabled={isLoading}
            >
              <RefreshCw className={`size-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Initial loading spinner */}
      {isLoading && !showContent && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center space-y-3">
            <Loader2 className="size-8 animate-spin text-purple-500 mx-auto" />
            <p className="text-gray-500 text-sm">Loading admin data…</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {showContent && (
        <div className="container mx-auto px-4 py-6 md:py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 w-full overflow-x-auto flex-nowrap p-1.5 bg-white border shadow-sm rounded-xl gap-1">
              {[
                { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                {
                  value: 'users',
                  icon: Users,
                  label: 'Users',
                  badge: profiles.length || null,
                  badgeClass: 'bg-gray-100 text-gray-700',
                },
                {
                  value: 'reports',
                  icon: AlertTriangle,
                  label: 'Reports',
                  badge: stats.pendingReports || null,
                  badgeClass: 'bg-orange-500 text-white border-0',
                },
                {
                  value: 'payouts',
                  icon: DollarSign,
                  label: 'Payouts',
                  badge: pendingPayouts || null,
                  badgeClass: 'bg-blue-500 text-white border-0',
                },
                {
                  value: 'stories',
                  icon: Heart,
                  label: 'Stories',
                  badge: stats.pendingStories || null,
                  badgeClass: 'bg-pink-500 text-white border-0',
                },
                {
                  value: 'support',
                  icon: Headphones,
                  label: 'Support',
                  badge: openTickets || null,
                  badgeClass: 'bg-purple-500 text-white border-0',
                },
                { value: 'plans', icon: Crown, label: 'Plans' },
              ].map(tab => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="flex-shrink-0 gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-blue-600 data-[state=active]:text-white"
                >
                  <tab.icon className="size-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.badge ? (
                    <Badge
                      className={`text-xs ml-0.5 ${tab.badgeClass || 'bg-gray-100 text-gray-700'}`}
                      variant="secondary"
                    >
                      {tab.badge}
                    </Badge>
                  ) : null}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ── Dashboard ── */}
            <TabsContent value="dashboard" className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
                  <p className="text-gray-500 mt-1 text-sm">Real-time platform statistics</p>
                </div>
                <Button
                  onClick={() => loadData()}
                  disabled={isLoading}
                  variant="outline"
                  size="sm"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 gap-1.5"
                >
                  <RefreshCw className={`size-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              <DashboardStats stats={stats} chartData={chartData} />

              {/* Quick Actions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="size-5 text-purple-600" /> Quick Actions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  {[
                    {
                      tab: 'reports', icon: AlertTriangle, bg: 'bg-orange-500',
                      gradient: 'from-orange-50 to-red-50', value: stats.pendingReports,
                      textColor: 'text-orange-600', title: 'Pending Reports',
                      desc: 'Review and resolve user reports',
                    },
                    {
                      tab: 'payouts', icon: DollarSign, bg: 'bg-blue-500',
                      gradient: 'from-blue-50 to-indigo-50', value: pendingPayouts,
                      textColor: 'text-blue-600', title: 'Pending Payouts',
                      desc: 'Process agent payment requests',
                    },
                    {
                      tab: 'stories', icon: Heart, bg: 'bg-pink-500',
                      gradient: 'from-pink-50 to-rose-50', value: stats.pendingStories,
                      textColor: 'text-pink-600', title: 'Pending Stories',
                      desc: 'Review success stories for approval',
                    },
                    {
                      tab: 'support', icon: Headphones, bg: 'bg-purple-500',
                      gradient: 'from-purple-50 to-violet-50', value: openTickets,
                      textColor: 'text-purple-600', title: 'Open Tickets',
                      desc: 'Respond to user support requests',
                    },
                  ].map(item => (
                    <Card
                      key={item.tab}
                      className={`border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer hover:-translate-y-1 bg-gradient-to-br ${item.gradient}`}
                      onClick={() => setActiveTab(item.tab)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className={`p-3 ${item.bg} rounded-xl shadow-lg`}>
                            <item.icon className="size-6 text-white" />
                          </div>
                          <span className={`text-4xl font-bold ${item.textColor}`}>{item.value}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{item.title}</h4>
                        <p className="text-sm text-gray-500 mt-1">{item.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Revenue Summary */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-600 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-80">Total Revenue</p>
                      <p className="text-4xl font-bold mt-1">
                        ${(stats.revenue || 0).toLocaleString()}
                      </p>
                      <p className="text-sm opacity-70 mt-1">
                        From {stats.paidUsers} premium subscribers
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">Agent Commissions Paid</p>
                      <p className="text-2xl font-bold mt-1">
                        ${(stats.agentCommissions || 0).toLocaleString()}
                      </p>
                      <p className="text-xs opacity-70 mt-1">80% commission rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Clear Data Button */}
              <div className="mt-6">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => setShowClearConfirm(true)}
                >
                  <Trash2 className="size-4 mr-1" />
                  Clear Test Data
                </Button>
              </div>

              {/* Clear Data Confirmation */}
              {showClearConfirm && (
                <div className="mt-4">
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-red-500 to-red-300 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="p-3 bg-red-500 rounded-xl shadow-lg">
                          <AlertOctagon className="size-6 text-white" />
                        </div>
                        <span className="text-4xl font-bold text-red-600">⚠️</span>
                      </div>
                      <h4 className="font-semibold text-gray-900">Clear All Test Data</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        This action will permanently delete all test data from the database.
                        Are you sure you want to proceed?
                      </p>
                      <div className="mt-4 flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-100 mr-2"
                          onClick={() => setShowClearConfirm(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-100"
                          onClick={handleClearAllData}
                          disabled={clearingData}
                        >
                          {clearingData ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Trash2 className="size-4 mr-1" />
                          )}
                          Clear Data
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* ── Users ── */}
            <TabsContent value="users">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {profiles.length} total users · Manage profiles and verifications
                </p>
              </div>
              <UserManagement
                profiles={profiles}
                onUpdateProfile={handleUpdateProfile}
                onDeleteProfile={handleDeleteProfile}
                onVerifyProfile={handleVerifyProfile}
              />
            </TabsContent>

            {/* ── Reports ── */}
            <TabsContent value="reports">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Reports Management</h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {reports.length} total · {stats.pendingReports} pending review
                </p>
              </div>
              <ReportsManagement
                reports={reports}
                profiles={profiles}
                onResolveReport={handleResolveReport}
              />
            </TabsContent>

            {/* ── Payouts ── */}
            <TabsContent value="payouts">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Agent Payouts</h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {payoutRequests.length} total · {pendingPayouts} pending · 80% commission biweekly
                </p>
              </div>
              <AgentPayouts
                payoutRequests={payoutRequests}
                onProcessPayout={handleProcessPayout}
              />
            </TabsContent>

            {/* ── Success Stories ── */}
            <TabsContent value="stories">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Success Stories Moderation
                </h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {successStories.length} total · {stats.pendingStories} pending approval
                </p>
              </div>
              <SuccessStoriesModeration
                stories={successStories}
                onApproveStory={handleApproveStory}
                onRejectStory={handleRejectStory}
                onDeleteStory={handleDeleteStory}
              />
            </TabsContent>

            {/* ── Support Tickets ── */}
            <TabsContent value="support">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Support Tickets</h2>
                <p className="text-gray-500 mt-1 text-sm">
                  {tickets.length} total · {openTickets} open
                </p>
              </div>
              <SupportTickets
                tickets={tickets}
                onRefresh={loadTickets}
                loading={ticketsLoading}
              />
            </TabsContent>

            {/* ── Plans ── */}
            <TabsContent value="plans">
              <PlansManagement />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

export default AdminApp;